// ManualPanel — panel lateral deslizante que muestra el Manual de
// Usuario del sistema. Tiene dos pestañas:
//   · 'Manual completo': el contenido estructurado de manualUsuario.js
//     (equivalente al .docx, con capítulos colapsables navegables).
//   · 'Por tema': la KB del asistente (temas cortos con respuesta
//     directa, útiles cuando buscas algo puntual).
//
// El panel se abre desde AssistantTab con un botón en su cabecera.
// Ocupa ~42% del ancho a la derecha en escritorio y pantalla completa
// en móvil. Se cierra con la X o clickeando el backdrop.

import { useState, useMemo } from 'react';
import { X, BookOpen, ListOrdered, ChevronDown, ChevronRight, Search as SearchIcon } from 'lucide-react';
import MANUAL from '../../data/manualUsuario';
import KB from '../../data/assistantKB';

export default function ManualPanel({ open, onClose }) {
  const [tab, setTab] = useState('completo'); // 'completo' | 'temas'
  const [openChapters, setOpenChapters] = useState(() => new Set([MANUAL[0]?.id]));
  const [openTopic, setOpenTopic] = useState(null); // id del tema abierto en la lista por-tema
  const [search, setSearch] = useState('');

  // Filtro simple por título/tema.
  const filteredManual = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return MANUAL;
    return MANUAL.filter(ch =>
      ch.title.toLowerCase().includes(q) ||
      ch.blocks.some(b => JSON.stringify(b).toLowerCase().includes(q))
    );
  }, [search]);

  const filteredKB = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return KB;
    return KB.filter(k =>
      k.tema.toLowerCase().includes(q) ||
      k.question.toLowerCase().includes(q) ||
      k.answer.toLowerCase().includes(q)
    );
  }, [search]);

  const toggleChapter = (id) => {
    setOpenChapters(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll  = () => setOpenChapters(new Set(MANUAL.map(c => c.id)));
  const collapseAll = () => setOpenChapters(new Set());

  if (!open) return null;

  return (
    <>
      {/* Backdrop: click fuera cierra el panel. z-index alto para
          cubrir todo el admin. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 9998,
        }}
      />
      {/* Panel */}
      <aside
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '42%', minWidth: 380, maxWidth: 720,
          background: '#fff',
          zIndex: 9999,
          display: 'flex', flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        }}
        role="dialog" aria-label="Manual de Usuario"
      >
        {/* Cabecera */}
        <div style={{
          padding: '14px 20px',
          background: 'var(--color-primary, #1B4332)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOpen size={20} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Manual de Usuario</div>
              <div style={{ fontSize: 11, opacity: 0.75 }}>Comercio Colaborativo</div>
            </div>
          </div>
          <button
            onClick={onClose}
            title="Cerrar (Esc)"
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              cursor: 'pointer', padding: 6, borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            <X size={22} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          background: '#fafafa',
        }}>
          <TabButton active={tab === 'completo'} onClick={() => setTab('completo')} icon={<BookOpen size={14} />}>
            Manual completo
          </TabButton>
          <TabButton active={tab === 'temas'} onClick={() => setTab('temas')} icon={<ListOrdered size={14} />}>
            Por tema
          </TabButton>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #eee', background: '#fff', position: 'relative' }}>
          <SearchIcon
            size={14}
            style={{ position: 'absolute', left: 26, top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'completo' ? 'Buscar en el manual…' : 'Buscar un tema…'}
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              border: '1px solid #ddd', borderRadius: 6, fontSize: 13,
              outline: 'none',
            }}
          />
          {tab === 'completo' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={expandAll} style={microBtnStyle}>Expandir todo</button>
              <button onClick={collapseAll} style={microBtnStyle}>Colapsar todo</button>
            </div>
          )}
        </div>

        {/* Contenido */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {tab === 'completo' ? (
            <ManualCompleto
              chapters={filteredManual}
              openChapters={openChapters}
              toggleChapter={toggleChapter}
            />
          ) : (
            <PorTema
              items={filteredKB}
              openTopic={openTopic}
              setOpenTopic={setOpenTopic}
            />
          )}
        </div>
      </aside>
    </>
  );
}

const microBtnStyle = {
  padding: '4px 10px', fontSize: 11,
  border: '1px solid #ddd', borderRadius: 999,
  background: '#fff', cursor: 'pointer', color: '#555',
};

function TabButton({ active, onClick, children, icon }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: '10px 12px',
        border: 'none', background: 'transparent',
        borderBottom: active ? '3px solid var(--color-primary, #1B4332)' : '3px solid transparent',
        color: active ? 'var(--color-primary, #1B4332)' : '#666',
        fontWeight: active ? 700 : 500,
        fontSize: 13, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

// ── Manual completo (capítulos con blocks) ────────────────────────

function ManualCompleto({ chapters, openChapters, toggleChapter }) {
  if (chapters.length === 0) {
    return <p style={{ color: '#999', textAlign: 'center', marginTop: 40 }}>Sin coincidencias.</p>;
  }
  return (
    <>
      {chapters.map(ch => {
        const isOpen = openChapters.has(ch.id);
        return (
          <div key={ch.id} style={{
            marginBottom: 8,
            border: '1px solid #eee',
            borderRadius: 6,
            overflow: 'hidden',
            background: '#fff',
          }}>
            <button
              onClick={() => toggleChapter(ch.id)}
              style={{
                width: '100%', padding: '10px 12px',
                background: '#f5f5f5', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                textAlign: 'left', fontWeight: 700, fontSize: 14, color: '#1B4332',
              }}
            >
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              {ch.title}
            </button>
            {isOpen && (
              <div style={{ padding: '12px 16px' }}>
                {ch.blocks.map((b, i) => <Block key={i} block={b} />)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function Block({ block }) {
  switch (block.type) {
    case 'p':
      return <p style={{ margin: '8px 0', lineHeight: 1.5, fontSize: 13, color: '#333' }}>{block.text}</p>;
    case 'h2':
      return <h3 style={{ margin: '14px 0 6px', fontSize: 14, color: '#2D6A4F', fontFamily: 'var(--font-serif)' }}>{block.text}</h3>;
    case 'bullet':
      return (
        <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
          {block.items.map((it, i) => (
            <li key={i} style={{ lineHeight: 1.5, fontSize: 13, color: '#333', marginBottom: 3 }}>{it}</li>
          ))}
        </ul>
      );
    case 'numbered':
      return (
        <ol style={{ paddingLeft: 22, margin: '6px 0' }}>
          {block.items.map((it, i) => (
            <li key={i} style={{ lineHeight: 1.5, fontSize: 13, color: '#333', marginBottom: 3 }}>{it}</li>
          ))}
        </ol>
      );
    case 'nota':
      return (
        <div style={{
          background: '#fff5e6', border: '1px solid #f5c78e',
          borderRadius: 6, padding: '10px 12px', margin: '10px 0',
          fontSize: 13, lineHeight: 1.5, color: '#a15c00',
        }}>
          <strong>💡 Nota:</strong> {block.text}
        </div>
      );
    case 'warn':
      return (
        <div style={{
          background: '#fdecea', border: '1px solid #f5c6c0',
          borderRadius: 6, padding: '10px 12px', margin: '10px 0',
          fontSize: 13, lineHeight: 1.5, color: '#a1000d',
        }}>
          <strong>⚠️ Importante:</strong> {block.text}
        </div>
      );
    case 'table':
      return (
        <div style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 12,
            background: '#fff',
          }}>
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} style={{
                    padding: '6px 10px', border: '1px solid #ddd',
                    background: '#1B4332', color: '#fff', textAlign: 'left',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                  {r.map((c, j) => (
                    <td key={j} style={{ padding: '6px 10px', border: '1px solid #eee', verticalAlign: 'top', lineHeight: 1.4 }}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

// ── Por tema (KB) ─────────────────────────────────────────────────

function PorTema({ items, openTopic, setOpenTopic }) {
  if (items.length === 0) {
    return <p style={{ color: '#999', textAlign: 'center', marginTop: 40 }}>Sin coincidencias.</p>;
  }
  return (
    <>
      {items.map(t => {
        const isOpen = openTopic === t.id;
        return (
          <div key={t.id} style={{
            marginBottom: 6,
            border: '1px solid #eee', borderRadius: 6, overflow: 'hidden',
            background: '#fff',
          }}>
            <button
              onClick={() => setOpenTopic(isOpen ? null : t.id)}
              style={{
                width: '100%', padding: '10px 12px',
                background: isOpen ? '#f0f7f4' : '#fff',
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#1B4332',
              }}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {t.tema}
            </button>
            {isOpen && (
              <div style={{ padding: '10px 14px', fontSize: 13, lineHeight: 1.5, color: '#333' }}>
                <div style={{ fontStyle: 'italic', color: '#666', marginBottom: 6 }}>
                  {t.question}
                </div>
                <div>{t.answer}</div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
